import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IntlProvider, FormattedMessage } from "react-intl";
import { useAuth } from '../../hooks/useAuth';
import {
  getOnboardingStatus,
  updateOnboardingProgress,
  createKosCreditor,
  setupIntegration,
  completeOnboarding,
  initiateDocumentSigning
} from '../services/onboardingApiService';
import OnboardingSetupContainer from "./OnboardingSetupContainer";
import BehalfModeIndicator from "../components/BehalfModeIndicator";
import { useOnboardingMode } from "../hooks/useOnboardingMode";
import { FlexColumn, THEME } from '../components/OnboardingLayoutComponents';
import LocaleContext from "../../state/contexts/LocaleContext";
import { messages } from "../onboarding.i18n";

const OnboardingSetupWrapper = () => {
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [completionSuccess, setCompletionSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [urlProcessed, setUrlProcessed] = useState(false);
  
  // Individual step states
  const [kosCreditorLoading, setKosCreditorLoading] = useState(false);
  const [kosCreditorError, setKosCreditorError] = useState(null);
  const [integrationSetupLoading, setIntegrationSetupLoading] = useState(false);
  const [integrationSetupError, setIntegrationSetupError] = useState(null);
  const [signingInitLoading, setSigningInitLoading] = useState(false);
  const [signingInitError, setSigningInitError] = useState(null);
  
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isBehalfMode, skipSignature, userRole } = useOnboardingMode();
  const locale = useContext(LocaleContext);

  // Extract submissionId from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('submissionId');
    // Only set submissionId if it's a valid string, otherwise keep it null
    const validId = id && id !== 'undefined' ? id : null;
    setSubmissionId(validId);
    setUrlProcessed(true); // Mark that we've processed the URL
  }, [location.search]);

  // Load onboarding status after URL is processed
  useEffect(() => {
    if (!urlProcessed) return; // Wait until URL is processed
    
    const loadOnboardingData = async () => {
      try {
        const statusResponse = await getOnboardingStatus(submissionId);
        setOnboardingData(statusResponse.data);
      } catch (error) {
        console.error('Error loading onboarding data:', error);
        setError(messages[locale].loadOnboardingError);
      } finally {
        setLoading(false);
      }
    };
    
    loadOnboardingData();
  }, [submissionId, urlProcessed, locale]);

  // Automatic signing initiation when setup loads
  useEffect(() => {
    if (!onboardingData || skipSignature || onboardingData.signingUrl) return;

    const initiateSigningProcess = async () => {
      setSigningInitLoading(true);
      setSigningInitError(null);

      try {
        const response = await initiateDocumentSigning(onboardingData.agreementDocumentUrl);

        // Update local state with signing URL and status
        // Note: API returns 'envelopeId' but we store as 'signingEnvelopeId' in state
        setOnboardingData(prev => ({
          ...prev,
          signingUrl: response.data.signingUrl,
          signingStatus: response.data.status,
          signingEnvelopeId: response.data.envelopeId
        }));
      } catch (error) {
        console.error('Error initiating signing:', error);
        const errorMessage = error.response?.data?.message || 'En feil oppstod under initiering av signering.';
        setSigningInitError(errorMessage);
      } finally {
        setSigningInitLoading(false);
      }
    };

    initiateSigningProcess();
  }, [onboardingData, skipSignature]);

  // Save progress functions for each section
  const saveSignatureProgress = async (signatureData) => {
    setSaving(true);
    try {
      // Prepare data for backend - include both old and new fields for backward compatibility
      const updateData = {
        hasSignedAgreement: signatureData.hasSignedAgreement,
        agreementDocumentUrl: signatureData.agreementDocumentUrl,
        signingStatus: signatureData.signingStatus,
        signingEnvelopeId: signatureData.envelopeId,
        signingUrl: signatureData.signingUrl,
        signedDocumentUrl: signatureData.signedDocumentUrl,
        completedDate: signatureData.completedDate
      };

      // Only send defined values to avoid sending undefined to backend
      const filteredUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      await updateOnboardingProgress(filteredUpdateData, submissionId);
      
      // Update local state with all signing-related fields
      setOnboardingData(prev => ({
        ...prev,
        ...filteredUpdateData
      }));
    } catch (error) {
      console.error('Error saving signature progress:', error);
      setError(messages[locale].saveSignatureError);
    } finally {
      setSaving(false);
    }
  };

  const saveSetupInfo = async (setupData) => {
    setSaving(true);
    try {
      await updateOnboardingProgress({
        companyName: setupData.companyName,
        postalAddress: setupData.postalAddress,
        postalNumber: setupData.postalNumber,
        postalPlace: setupData.postalPlace,
        webDisplayName: setupData.webDisplayName,
        webUserName: setupData.webUserName
      }, submissionId);
      
      // Update local state
      setOnboardingData(prev => ({
        ...prev,
        companyName: setupData.companyName,
        postalAddress: setupData.postalAddress,
        postalNumber: setupData.postalNumber,
        postalPlace: setupData.postalPlace,
        webDisplayName: setupData.webDisplayName,
        webUserName: setupData.webUserName
      }));
    } catch (error) {
      console.error('Error saving setup info:', error);
      setError(messages[locale].saveCompanyInfoError);
    } finally {
      setSaving(false);
    }
  };

  const saveAccountingSystemInfo = async (updateData) => {
    try {
      // Convert clientSystem to string if it's a number, keep null as null
      const processedData = {
        ...updateData,
        ...(updateData.clientSystem !== undefined && {
          clientSystem: updateData.clientSystem === null ? null : String(updateData.clientSystem)
        })
      };
      
      await updateOnboardingProgress(processedData, submissionId);
      
      // Update local state with the new data
      setOnboardingData(prev => ({
        ...prev,
        ...updateData
      }));
    } catch (error) {
      console.error('Error saving accounting system info:', error);
      setError(messages[locale].saveAccountingSystemError);
    }
  };

  const handleCreateKosCreditor = async (formData) => {
    setKosCreditorLoading(true);
    setKosCreditorError(null);
    
    try {
      // First save the form data via update endpoint
      await saveAccountingSystemInfo(formData);
      
      // Then create the KOS creditor
      const response = await createKosCreditor(true, submissionId);
      
      // Update local state with the response data
      setOnboardingData(prev => ({
        ...prev,
        ...formData, // Include the saved form data
        kosCreditorCreated: true,
        creditorNo: response.data.creditorNo,
        actorId: response.data.actorId,
        integrationRequired: response.data.integrationRequired
      }));
    } catch (error) {
      console.error('Error creating KOS creditor:', error);
      setKosCreditorError(
        error.response?.data?.message || messages[locale].createKosCreditorError
      );
    } finally {
      setKosCreditorLoading(false);
    }
  };

  const handleSetupIntegration = async (integrationData) => {
    setIntegrationSetupLoading(true);
    setIntegrationSetupError(null);
    
    try {
      // First save the integration data via update endpoint
      await saveAccountingSystemInfo(integrationData);
      
      // Then setup the integration
      await setupIntegration(submissionId);
      
      // Update local state
      setOnboardingData(prev => ({
        ...prev,
        ...integrationData, // Include the saved integration data
        integrationSetupCompleted: true
      }));
    } catch (error) {
      console.error('Error setting up integration:', error);
      setIntegrationSetupError(
        error.response?.data?.message || messages[locale].setupIntegrationError
      );
    } finally {
      setIntegrationSetupLoading(false);
    }
  };

  const completeSetup = async () => {
    setSaving(true);
    setError("");
    setCompletionSuccess(false);
    
    try {
      const response = await completeOnboarding(true, submissionId);
      
      // Show success message
      setCompletionSuccess(true);
      setSaving(false);
      
      // Delay redirect by 3 seconds to show success message
      setTimeout(() => {
        if (isBehalfMode) {
          // In behalf mode, accountant stays logged in as themselves
          // Just redirect to client management page
          navigate('/client-onboardings');
        } else {
          // Regular onboarding - check if token exists and sign in with new role
          if (!response.data?.token) {
            console.error('No token in response:', response.data);
            setError(messages[locale].noTokenError);
            return;
          }
          
          // Use the refreshed token to automatically log in with new role
          auth.tokenSignIn(response.data.token);
          // Force full page reload to ensure complete state reset
          window.location.href = '/#/Overview';
        }
      }, 3000);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError(
        error.response?.data?.message || messages[locale].completeOnboardingError
      );
      setSaving(false);
    }
  };

  // Additional safety check for auth state
  if (!auth.isAuthenticated || (!auth.isOnboarding() && !isBehalfMode)) {
    console.error('OnboardingSetupWrapper: Invalid auth state', {
      isAuthenticated: auth.isAuthenticated,
      isOnboarding: auth.isOnboarding(),
      isBehalfMode: isBehalfMode,
      role: auth.role
    });
    return (
      <IntlProvider locale={locale} messages={messages[locale] || messages.nb}>
        <div style={{ textAlign: "center", padding: THEME.spacing.mega, color: THEME.colors.error }}>
          <FormattedMessage id="invalidAccess" />
          <br />
          <button onClick={() => navigate('/login')} style={{ marginTop: THEME.spacing.lg }}>
            <FormattedMessage id="goToLogin" />
          </button>
        </div>
      </IntlProvider>
    );
  }

  if (loading) {
    return (
      <IntlProvider locale={locale} messages={messages[locale] || messages.nb}>
        <div style={{ textAlign: "center", padding: THEME.spacing.mega }}>
          <FormattedMessage id="loadingOnboardingData" />
        </div>
      </IntlProvider>
    );
  }

  if (error && !onboardingData) {
    return (
      <div style={{ textAlign: "center", padding: THEME.spacing.mega, color: THEME.colors.error }}>
        {error}
      </div>
    );
  }

  return (
    <IntlProvider locale={locale} messages={messages[locale] || messages.nb}>
      <FlexColumn alignItems="center">
        {isBehalfMode && (
          <BehalfModeIndicator 
            clientName={onboardingData?.companyName}
            userRole={userRole}
          />
        )}
        
        <OnboardingSetupContainer
          onboardingData={onboardingData}
          saving={saving}
          error={error}
          completionSuccess={completionSuccess}
          saveSignatureProgress={saveSignatureProgress}
          saveSetupInfo={saveSetupInfo}
          saveAccountingSystemInfo={saveAccountingSystemInfo}
          completeSetup={completeSetup}
          onCreateKosCreditor={handleCreateKosCreditor}
          kosCreditorLoading={kosCreditorLoading}
          kosCreditorError={kosCreditorError}
          onSetupIntegration={handleSetupIntegration}
          integrationSetupLoading={integrationSetupLoading}
          integrationSetupError={integrationSetupError}
          signingInitLoading={signingInitLoading}
          signingInitError={signingInitError}
          submissionId={submissionId}
          skipSignature={skipSignature}
        />
      </FlexColumn>
    </IntlProvider>
  );
};

export default OnboardingSetupWrapper;